import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

const FeaturesTable: React.FC = () => {
  const { t } = useTranslation('pricing');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = t('featuresTable', { returnObjects: true }) as Array<{
    feature: string;
    singlePage: boolean;
    basic: boolean;
    pro: boolean;
    enterprise: boolean;
  }>;

  const CheckIcon = () => (
    <svg
      className="w-6 h-6 text-primary-500 mx-auto"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );

  const CrossIcon = () => (
    <svg
      className="w-6 h-6 text-gray-300 dark:text-dark-border mx-auto"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  return (
    <section className="py-20" ref={ref}>
      <div className="container-custom">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
            {t('featuresTableSection.title')}
          </h2>
          <p className="text-xl text-secondary max-w-2xl mx-auto">
            {t('featuresTableSection.subtitle')}
          </p>
        </motion.div>

        <motion.div
          className="card overflow-x-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border">
                <th className="text-left p-4 font-bold">
                  {t('featuresTableSection.feature')}
                </th>
                <th className="text-center p-4 font-bold text-sm">
                  {t('featuresTableSection.singlePage')}
                </th>
                <th className="text-center p-4 font-bold">
                  {t('featuresTableSection.basic')}
                </th>
                <th className="text-center p-4 font-bold text-primary-500">
                  {t('featuresTableSection.pro')}
                </th>
                <th className="text-center p-4 font-bold">
                  {t('featuresTableSection.enterprise')}
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-200 dark:border-dark-border last:border-0"
                >
                  <td className="p-4 text-secondary">
                    {feature.feature}
                  </td>
                  <td className="p-4 text-center">
                    {feature.singlePage ? <CheckIcon /> : <CrossIcon />}
                  </td>
                  <td className="p-4 text-center">
                    {feature.basic ? <CheckIcon /> : <CrossIcon />}
                  </td>
                  <td className="p-4 text-center bg-primary-900/10">
                    {feature.pro ? <CheckIcon /> : <CrossIcon />}
                  </td>
                  <td className="p-4 text-center">
                    {feature.enterprise ? <CheckIcon /> : <CrossIcon />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesTable;
